/**
 * Mass–energy equivalence and relativistic kinetic energy.
 *
 *   E₀ = m c²                      rest energy of a mass m
 *   E = γ m c²                     total energy of a moving particle
 *   K = (γ − 1) m c²               its kinetic energy  (→ ½ m v² for v ≪ c)
 *
 * SI units; c = 299 792 458 m/s exactly. γ − 1 comes from γ²β²/(γ + 1), so a slow
 * particle's kinetic energy is not lost to rounding.
 */

import {
  SPEED_OF_LIGHT,
  TNT_TON_JOULES,
} from "@/lib/tool-kit/physics-fields/constants";
import {
  betaFromGamma,
  gammaMinusOne,
  lorentzFactor,
} from "@/lib/tool-kit/physics-fields/relativity";

const C_SQUARED = SPEED_OF_LIGHT ** 2;

export type MassEnergyError =
  | "missing"
  | "non-positive"
  | "negative"
  | "light-speed"
  | "not-finite";

interface Failure {
  ok: false;
  error: MassEnergyError;
}

/** E = m c², in joules for a mass in kilograms. */
export function restEnergy(mass: number): number {
  return mass * C_SQUARED;
}

/** m = E / c², in kilograms for an energy in joules. */
export function massOf(energy: number): number {
  return energy / C_SQUARED;
}

/* ── Mass ↔ energy ────────────────────────────────────────────────────────── */

export type RestTarget = "energy" | "mass";

export interface RestSolution {
  mass: number;
  energy: number;
  /** The energy in tonnes of TNT (4.184 GJ each). */
  tntTonnes: number;
}

export function solveRest(
  mass: number | null,
  energy: number | null,
  target: RestTarget
): { ok: true; value: RestSolution } | Failure {
  const input = target === "energy" ? mass : energy;
  if (input === null) {
    return { ok: false, error: "missing" };
  }
  if (!(input > 0)) {
    return { ok: false, error: "non-positive" };
  }
  const solvedMass = target === "energy" ? input : massOf(input);
  const solvedEnergy = target === "energy" ? restEnergy(input) : input;
  const solution = {
    mass: solvedMass,
    energy: solvedEnergy,
    tntTonnes: solvedEnergy / TNT_TON_JOULES,
  };
  if (!Object.values(solution).every((v) => Number.isFinite(v) && v > 0)) {
    return { ok: false, error: "not-finite" };
  }
  return { ok: true, value: solution };
}

/* ── A moving particle ────────────────────────────────────────────────────── */

export type MovingKnown = "speed" | "kinetic";

export interface MovingSolution {
  mass: number;
  /** Speed, m/s. */
  speed: number;
  beta: number;
  gamma: number;
  restEnergy: number;
  totalEnergy: number;
  /** (γ − 1) m c², J. */
  kinetic: number;
  /** ½ m v² for the same speed, J. */
  classicalKinetic: number;
  /** K / (½ m v²): 1 at low speed, growing without bound. */
  ratio: number;
  /** How far below the speed of light, c − v = c / (γ² (1 + β)), m/s. */
  shortfall: number;
}

export function solveMoving(
  mass: number | null,
  known: MovingKnown,
  value: number | null
): { ok: true; value: MovingSolution } | Failure {
  if (mass === null || value === null) {
    return { ok: false, error: "missing" };
  }
  if (!(mass > 0)) {
    return { ok: false, error: "non-positive" };
  }
  if (value < 0) {
    return { ok: false, error: "negative" };
  }
  const rest = restEnergy(mass);
  let beta: number;
  let gamma: number;
  if (known === "speed") {
    beta = value / SPEED_OF_LIGHT;
    if (beta >= 1) {
      return { ok: false, error: "light-speed" };
    }
    gamma = lorentzFactor(beta);
  } else {
    gamma = 1 + value / rest;
    beta = betaFromGamma(gamma);
  }
  const speed = beta * SPEED_OF_LIGHT;
  const kinetic = known === "kinetic" ? value : gammaMinusOne(beta) * rest;
  const classical = 0.5 * mass * speed ** 2;
  const solution: MovingSolution = {
    mass,
    speed,
    beta,
    gamma,
    restEnergy: rest,
    totalEnergy: rest + kinetic,
    kinetic,
    classicalKinetic: classical,
    ratio: classical === 0 ? 1 : kinetic / classical,
    shortfall: SPEED_OF_LIGHT / (gamma * gamma * (1 + beta)),
  };
  if (!Object.values(solution).every(Number.isFinite)) {
    return { ok: false, error: "not-finite" };
  }
  return { ok: true, value: solution };
}

/**
 * The largest unit among `ids` in which `value` is at least 1, or the smallest if it is
 * below them all — "21.48 kt TNT", "938.3 MeV". `ids` must run from small to large.
 */
export function pickUnit(
  units: readonly { id: string; factor: number }[],
  ids: readonly string[],
  value: number
): string {
  let chosen = ids[0];
  for (const id of ids) {
    const unit = units.find((u) => u.id === id);
    if (unit && value / unit.factor >= 1) {
      chosen = id;
    }
  }
  return chosen;
}
